import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, Plus, Wrench } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import QuickEntry from "@/components/quick/QuickEntry";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import { comingUp, needsAttention, recentActivity } from "@/lib/attention";
import { updateReminder } from "@/services/dataService";
import { useToast } from "@/hooks/use-toast";
import { categoryLabel } from "@/lib/categories";
import {
  BUSINESS,
  buildLedgerRows,
  currentMonthKey,
  formatMoney,
  houseName,
  monthByHouse,
  monthLabel,
  shiftMonth,
} from "@/lib/ledger";

function greeting(now = new Date()) {
  const hour = now.getHours();
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

const money = (value) => formatMoney(value).replace(/\.00$/, "");

const DAY_MS = 86400000;
function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}
/** "Today", "Tomorrow", "Fri", or "Nov 12". */
function dueLabel(ts) {
  const diff = Math.floor((ts - startOfToday()) / DAY_MS);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff > 1 && diff < 7) return new Date(ts).toLocaleDateString("en-US", { weekday: "short" });
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
/** "Just now", "2h ago", "Yesterday", "Nov 1". */
function whenLabel(ts) {
  const minutes = Math.floor((Date.now() - ts) / 60000);
  if (minutes < 2) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (ts >= startOfToday()) return `${Math.floor(minutes / 60)}h ago`;
  if (ts >= startOfToday() - DAY_MS) return "Yesterday";
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [month, setMonth] = useState(currentMonthKey());
  const { data, isLoading, error, refresh } = usePortfolioData([
    "properties",
    "tenants",
    "expenses",
    "rentPayments",
    "maintenance",
    "insurance",
    "licenses",
    "reminders",
  ]);
  const { toast } = useToast();
  const completeReminder = async (id) => {
    try {
      await updateReminder({ id, isCompleted: true });
      refresh();
    } catch (e) {
      toast({ title: "Couldn't update", description: e.message, variant: "destructive" });
    }
  };

  const rows = useMemo(() => buildLedgerRows(data), [data]);
  const attention = useMemo(() => needsAttention(data), [data]);
  const upcoming = useMemo(() => comingUp(data).slice(0, 8), [data]);
  const activity = useMemo(() => recentActivity(data, 8), [data]);
  const summary = useMemo(() => monthByHouse(rows, data.properties, month), [rows, data.properties, month]);
  const propertiesById = useMemo(() => new Map(data.properties.map((p) => [String(p._id), p])), [data.properties]);
  const firstName = (currentUser?.name || "").split(" ")[0];

  return (
    <PageLayout
      title={`${greeting()}${firstName ? `, ${firstName}` : ""}`}
      actions={
        <>
          <Button variant="outline" size="sm" asChild className="gap-1.5">
            <Link to="/maintenance">
              <Wrench className="h-4 w-4" /> Repair
            </Link>
          </Button>
          <Button size="sm" asChild className="gap-1.5">
            <Link to="/ledger">
              <Plus className="h-4 w-4" /> Add entry
            </Link>
          </Button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-destructive">Couldn't load everything: {error.message}</p> : null}

      <section className="hh-panel mb-5 p-4">
        <h2 className="hh-section-title mb-2">What happened today?</h2>
        <QuickEntry />
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className="min-w-0 space-y-5">
          <section className="hh-panel p-4">
            <h2 className="hh-section-title mb-3">
              Needs attention{attention.length ? <span className="ml-1.5 text-muted-foreground">({attention.length})</span> : null}
            </h2>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : attention.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" /> All clear. Nothing needs you today.
              </p>
            ) : (
              <ul className="divide-y">
                {attention.slice(0, 6).map((item) => (
                  <li key={item.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${item.tone === "urgent" ? "bg-rose-500" : "bg-amber-500"}`}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{item.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">{item.detail}</span>
                    </span>
                    {item.reminderId ? (
                      <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => completeReminder(item.reminderId)}>
                        {item.action}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" asChild className="h-8 shrink-0">
                        <Link to={item.to}>{item.action}</Link>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="hh-panel p-4">
            <h2 className="hh-section-title mb-3">Coming up</h2>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing in the next 90 days. Type “need to … tomorrow at …” above to add a to-do.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {upcoming.map((item) => (
                  <li key={item.id} className="flex items-center gap-3">
                    <span className="w-16 shrink-0 tabular-nums text-muted-foreground">{dueLabel(item.date)}</span>
                    <span className="min-w-0 flex-1 truncate">
                      {item.title}
                      {item.where ? <span className="text-muted-foreground"> · {item.where}</span> : null}
                    </span>
                    {item.reminderId ? (
                      <button
                        type="button"
                        onClick={() => completeReminder(item.reminderId)}
                        className="shrink-0 rounded-md border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        Done
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section className="hh-panel overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 pt-4">
            <h2 className="hh-section-title">{monthLabel(month)}</h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="hh-table mt-2">
              <thead>
                <tr>
                  <th>House</th>
                  <th className="num">In</th>
                  <th className="num">Out</th>
                  <th className="num">Net</th>
                </tr>
              </thead>
              <tbody>
                {summary.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="max-w-[180px] truncate">
                      <Link
                        to={line.id === BUSINESS ? `/ledger?m=${month}&house=${BUSINESS}` : `/houses/${line.id}`}
                        className="hover:underline"
                      >
                        {line.name}
                      </Link>
                    </td>
                    <td className="num">{line.moneyIn ? money(line.moneyIn) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="num">{line.moneyOut ? money(line.moneyOut) : <span className="text-muted-foreground">—</span>}</td>
                    <td className={`num font-medium ${line.net < 0 ? "text-rose-700 dark:text-rose-300" : ""}`}>
                      {formatMoney(line.net, { sign: true }).replace(/\.00$/, "")}
                    </td>
                  </tr>
                ))}
                {!summary.lines.length ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                      {isLoading ? "Loading…" : "Add your houses to see each one's month."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num">{money(summary.total.moneyIn)}</td>
                  <td className="num">{money(summary.total.moneyOut)}</td>
                  <td className={`num ${summary.total.net < 0 ? "text-rose-700 dark:text-rose-300" : ""}`}>
                    {formatMoney(summary.total.net, { sign: true }).replace(/\.00$/, "")}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="px-4 py-3">
            <Link to={`/ledger?m=${month}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              Open {monthLabel(month).split(" ")[0]} in the ledger <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>

      <section className="hh-panel mt-5 overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4">
          <h2 className="hh-section-title">Recent activity</h2>
          <Link to="/ledger?m=all" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            Ledger <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <ul className="mt-2 divide-y">
          {activity.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="w-20 shrink-0 text-xs text-muted-foreground">{whenLabel(item.at)}</span>
              <span className="w-24 shrink-0 text-xs font-medium text-muted-foreground">{item.type}</span>
              <span className="min-w-0 flex-1 truncate">
                {item.title}
                {item.where ? <span className="text-muted-foreground"> · {item.where}</span> : null}
                {item.due ? <span className="text-muted-foreground"> · due {dueLabel(item.due)}</span> : null}
              </span>
              {Number.isFinite(item.amount) ? (
                <span className={`shrink-0 tabular-nums ${item.amount > 0 ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                  {formatMoney(item.amount, { sign: true })}
                </span>
              ) : null}
            </li>
          ))}
          {!activity.length ? (
            <li className="px-4 py-8 text-center text-sm text-muted-foreground">
              {isLoading ? "Loading…" : "Nothing yet. Tell me what happened in the box above."}
            </li>
          ) : null}
        </ul>
      </section>
    </PageLayout>
  );
}
